import { Either, Schema } from "effect";
import {
	DEFAULT_DEV_AUTH_ORIGINS,
	expandWwwApexPairs,
	isHttpsOrigin,
	isHttpsUrl,
	isLocalNetworkOrigin,
	parseOriginCsv,
	toOrigin,
} from "./origins";

export type AuthEnvSource = Record<string, string | undefined>;

export const BetterAuthSecretSchema = Schema.String.pipe(
	Schema.minLength(32),
);

export function shouldEnforceStrictProductionAuth(env: AuthEnvSource): boolean {
	const nodeEnv = env.NODE_ENV ?? "development";
	return nodeEnv === "production";
}

export function resolveBetterAuthBaseUrl(
	env: AuthEnvSource,
	options: { developmentFallback: string } = { developmentFallback: "http://localhost:3001" },
): string {
	const enforceStrictProduction = shouldEnforceStrictProductionAuth(env);
	const siteUrl =
		env.SITE_URL ??
		env.BETTER_AUTH_BASE_URL ??
		(enforceStrictProduction ? undefined : options.developmentFallback);

	if (!siteUrl) {
		throw new Error("SITE_URL (or BETTER_AUTH_BASE_URL) must be configured in production");
	}

	if (enforceStrictProduction && !isHttpsUrl(siteUrl)) {
		throw new Error("SITE_URL must use https in production");
	}

	return siteUrl;
}

export function resolveBetterAuthSecret(env: AuthEnvSource): string | undefined {
	const secret = env.BETTER_AUTH_SECRET ?? env.AUTH_SECRET;

	if (!shouldEnforceStrictProductionAuth(env)) {
		return secret;
	}

	if (!secret) {
		throw new Error("BETTER_AUTH_SECRET must be configured in production");
	}

	const decoded = Schema.decodeUnknownEither(BetterAuthSecretSchema)(secret);
	if (Either.isLeft(decoded)) {
		throw new Error("BETTER_AUTH_SECRET must be at least 32 characters in production");
	}

	return decoded.right;
}

export function resolveTrustedAuthOrigins(env: AuthEnvSource): string[] {
	const enforceStrictProduction = shouldEnforceStrictProductionAuth(env);
	const origins = new Set<string>();

	for (const list of [
		env.AUTH_TRUSTED_ORIGINS,
		env.BETTER_AUTH_TRUSTED_ORIGINS,
		env.BUN_AUTH_PROXY_ALLOWED_ORIGINS,
	]) {
		for (const origin of parseOriginCsv(list)) {
			origins.add(origin);
		}
	}

	for (const origin of [
		toOrigin(env.CONVEX_SITE_URL),
		toOrigin(env.BUN_PUBLIC_CONVEX_SITE_URL),
		toOrigin(env.SITE_URL),
		toOrigin(env.BETTER_AUTH_BASE_URL),
		toOrigin(env.BUN_PUBLIC_APP_ORIGIN),
		toOrigin(env.BUN_AUTH_PROXY_ORIGIN),
	]) {
		if (origin) {
			origins.add(origin);
		}
	}

	if (!enforceStrictProduction) {
		for (const origin of DEFAULT_DEV_AUTH_ORIGINS) {
			origins.add(origin);
		}
	}

	expandWwwApexPairs(origins);

	const trustedOrigins = [...origins];

	if (enforceStrictProduction) {
		if (trustedOrigins.length === 0) {
			throw new Error(
				"No trusted auth origins configured. Set AUTH_TRUSTED_ORIGINS (comma-separated https origins).",
			);
		}

		const insecureOrigins = trustedOrigins.filter(
			(origin) => !isHttpsOrigin(origin) && !isLocalNetworkOrigin(origin),
		);
		if (insecureOrigins.length > 0) {
			throw new Error(
				`All trusted auth origins must use https in production. Invalid: ${insecureOrigins.join(", ")}`,
			);
		}
	}

	return trustedOrigins;
}
